import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { fetchRoundByNumber } from '../archive/api';
import { RoundAnchorCards } from '../verify/RoundAnchorCards';
import { RoundDigitsTable } from '../verify/RoundDigitsTable';
import { blockDigitRows, drawDigits, roundCloseTimestamp } from '../verify/roundDigits';
import { useI18nStore } from '../../shared/i18n/i18nStore';
import { ticketLabel } from '../../shared/raffle/ticketSerial';
import { moduloDecimalString } from '../../shared/lib/raffleDrawnSerial';
import { serialWrap } from '../../shared/raffle/historyRowView';
import { shortDateTime } from '../../shared/lib/datetime';
import { fmtInt } from '../../shared/lib/format';
import type { RaffleRecentEvent } from '../wallet/api';

/**
 * 「这个号从哪来」——点盾牌进来的一页。
 *
 * <p><b>倒过来讲。</b> 2026-09-17 PM：这一页原先按**生产顺序**叙述——先讲三个
 * 区块怎么选（连带一整块「为什么是这三个区块」的解释），再讲十位数怎么造出来
 * （10 列 × 5 行的算术表），最后才说中奖号码。于是玩家要翻到第三屏才看到自己
 * 来找的那个数，整页 3.9 屏。读者是按**怀疑顺序**来的：开出什么 → 凭什么 →
 * 这数哪来的。现在三层就是这个顺序，前两层各半屏，第三层默认折叠。
 *
 * <p><b>那张 10 列算术表搬进了折叠层。</b> 它回答的是「十位数怎么造出来的」，
 * 而没有一个玩家是带着这个问题来的。较真的人点开，一条不少。
 *
 * <p><b>没有别处可去。</b> 早期原型有一个「去看这个号是哪来的」按钮，点开跳去
 * 归档明细页才能看到推导——玩家问出那句话的那一刻，答案必须已经在眼前。这一
 * 屏就是要顶替、消灭那一层跳转，唯一的导航是返回键。
 */
export function DrawVerifyOverlay({ event, rowSerial, onClose }: {
  event: RaffleRecentEvent;
  /**
   * History 行自己算出的、权威的开奖号（`historyRow(event).drawnSerial`）——
   * 从那一行原样带过来，不在这里重算。这一页自己也会独立算一遍
   * `D mod N`；两者理应永远相等（同一个 D、同一个 N），但万一哪天真的
   * 对不上——`historyRowView.ts` 曾经因为线上序号的 0/1 起点没换算对而和这里
   * 差 1（2026-09-13 复核）——绝不能悄悄只显示其中一个，见下面 mismatch 的
   * 渲染。
   */
  rowSerial: number | null;
  onClose: () => void;
}) {
  const t = useI18nStore((state) => state.t);
  const language = useI18nStore((state) => state.language);
  // 第三层默认折叠。**默认值不许改成 true**：一打开就铺开三个区块卡和那张
  // 10 列表，等于回到 2026-09-17 之前那个「先讲区块」的版本。
  const [showSource, setShowSource] = useState(false);

  const anchorRound = Number(event.anchor_round_number || 0);

  const roundQuery = useQuery({
    queryKey: ['sorteios-verify-round', anchorRound],
    queryFn: () => fetchRoundByNumber(anchorRound),
    enabled: anchorRound > 0,
    staleTime: 5 * 60_000,
  });

  const round = roundQuery.data;
  // 一期由「本地日期时刻 + global 号」认。**不带日内期号**：它和票号都是
  // 「#三位数」，同屏出现玩家分不清哪个是期、哪个是中奖的票，而 global 号本来
  // 就是客服工单和链上核对要的那个（2026-09-18 PM）。
  const identityLine = [
    event.scheduled_at ? shortDateTime(event.scheduled_at, language) : '',
    anchorRound ? `global #${anchorRound}` : '',
  ].filter(Boolean).join(' · ');

  // 十位公共数字。draw_digits 可能是数组，必须走 drawDigits()，不许现场
  // String() 一遍（那会把数组拼成 "3,6,5,4,…"）。
  const digits = round ? drawDigits(round) : '';
  const blockDigits = round ? blockDigitRows(round) : [];

  // 新期票号从 0 起，保留的旧期从 1 起；serial_base 决定余数到票号的映射。
  // D 远超 Number.MAX_SAFE_INTEGER，必须走 BigInt 的 moduloDecimalString。
  const totalEntries = Number(event.total_entries || 0);
  const serialBase = event.serial_base === 1 ? 1 : 0;
  const remainder = digits && totalEntries > 0 ? moduloDecimalString(digits, totalEntries) : null;
  const first = remainder === null ? null : remainder + serialBase;
  // 两边都能算出号码时才比——任何一边是 null（正在加载、数据缺失）都不构成矛盾。
  const mismatch = rowSerial !== null && first !== null && rowSerial !== first;
  // 顶上那个数以**权威值**为准（History 那一行显示的、也是官方记录的那个）。
  // 没带过来时才退回自己算的。
  const shown = rowSerial ?? first;

  // 连号顺延，与 History 行、参与名单的中奖标记共用同一份 serialWrap。
  //
  // count 只能来自 winnerCount 本身，不能再兜底成 8、也不能截到 8：
  // winnerCount === 0（`0 || 8` 这条老写法算出来是 8）——一期 History 那一行
  // 自己标着「没人中」，这一步却画出 8 个从没付过钱的号；winnerCount > 8——
  // 阿根廷的档是 14 个中奖者、巴西 50 参与档是 16 个，静默截到 8 个，Sheet 里
  // 明明写着「Winners: 14」（2026-09-13 复核 Important ×2）。
  //
  // mismatch 为真时也不画——这些号全部是从 first（有争议的那个值）顺延出来的，
  // first 本身已经和权威的 rowSerial 对不上，顺延出来的号只会更错。
  const winnerCount = (event.winners || []).length;
  const serials = first === null || mismatch || winnerCount <= 0
    ? [] : serialWrap(first, totalEntries, winnerCount, serialBase);

  return (
    <section className="srt-verify-screen">
      <header className="ds-header shrink-0">
        <button className="icon-button tap" type="button" onClick={onClose} aria-label={t('back')}>
          <ChevronLeft size={20} aria-hidden="true" />
        </button>
        <h1 className="flex-1 text-[20px] leading-[26px] font-extrabold">
          {t('sorteiosHistoryVerify')}
        </h1>
        <span className="icon-button" aria-hidden="true"><ShieldCheck size={18} /></span>
      </header>

      <div className="srt-verify-body no-scrollbar">
        {identityLine ? <p className="srt-verify-ident">{identityLine}</p> : null}

        {roundQuery.isError || !anchorRound ? (
          <div className="milhar-stats-fallback">
            <p>{t('milharVerifyUnavailable')}</p>
            <button type="button" className="milhar-verify-retry tap"
              onClick={() => roundQuery.refetch()}>{t('milharStatsRetry')}</button>
          </div>
        ) : !round ? (
          <div className="milhar-stats-skeleton">
            {Array.from({ length: 4 }, (_, i) => <span key={i} className="sk" />)}
          </div>
        ) : (
          <>
            {/* ── 第 1 层：开出什么 ───────────────────────────────────── */}
            {shown !== null ? (
              <div className="srt-verify-result">
                <span className="srt-verify-result-label">{t('sorteiosVerifyResultTitle')}</span>
                <b className="srt-verify-result-no">{ticketLabel(shown)}</b>
                {totalEntries > 0 ? (
                  <span className="srt-verify-result-sub">
                    {t('sorteiosVerifyResultEntries').replace('%n%', fmtInt(totalEntries))}
                  </span>
                ) : null}
                {/* 矛盾提示必须在结果旁边，而不是埋在算式那一层里——这一页最
                    重要的一句话就是「我们自己都算不出屏幕上这个数」，它不能
                    需要往下滑才看得到（2026-09-17 评审）。 */}
                {mismatch ? (
                  <div className="srt-verify-mismatch">
                    <p className="milhar-verify-mismatch">{t('sorteiosVerifyMismatch')}</p>
                    {/* 两个号都得摆出来，标清楚哪个是权威值——一句「和上面对不
                        上」却不露出「上面」是哪个数，等于在自证矛盾时把矛盾的
                        另一半藏起来（2026-09-13 复核 Important）。 */}
                    <div className="srt-verify-mismatch-nums">
                      <span>
                        <b>{t('sorteiosVerifyOfficial')}</b>
                        <em>{ticketLabel(rowSerial)}</em>
                      </span>
                      <span>
                        <b>{t('sorteiosVerifyRecomputed')}</b>
                        <em>{ticketLabel(first)}</em>
                      </span>
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* ── 第 2 层：凭什么是这个数 ─────────────────────────────
                一行箭头图，带这一期的真数字。原先这里是一句 `%d% ÷ %n% →
                remainder %r%` 的等式加一句结论，两行等宽文字——正确但读起来
                是一段话。现在是图：数字在上，箭头向下，结果在下。 */}
            {remainder !== null && first !== null ? (
              <div className="srt-verify-card">
                <div className="srt-verify-seclabel">{t('sorteiosVerifyWhyTitle')}</div>
                <div className="srt-verify-flow">
                  <span className="srt-verify-flow-d">{digits}</span>
                  <span className="srt-verify-flow-cap">{t('sorteiosVerifyDigitsLabel')}</span>
                  <span className="srt-verify-flow-arrow" aria-hidden="true">&#8595;</span>
                  <span className="srt-verify-flow-op">
                    <em>{t('sorteiosVerifyDivide').replace('%n%', fmtInt(totalEntries))}</em>
                    <i aria-hidden="true">&#8594;</i>
                    <em>{t('sorteiosVerifyRemainder')} <b>{remainder}</b></em>
                    {serialBase === 1 ? <em>{t('sorteiosVerifyTicketOffset')}</em> : null}
                  </span>
                  <span className="srt-verify-flow-arrow" aria-hidden="true">&#8595;</span>
                  {/* 余数本身就是票号，这一格不再有算式。以前是「余数 + 1 =」
                      外加一句解释那个 +1 的话——两者都只为那一步存在，票号改成
                      0 起之后一起消失（2026-09-24 PM）。留下的一句话讲的是编号
                      约定（票从 000 开始），看一次就懂，不用每次核对都重新理解。 */}
                  <span className="srt-verify-flow-out">
                    <b>{ticketLabel(first)}</b>
                  </span>
                </div>
                <p className="srt-verify-hint">
                  {t(serialBase === 1 ? 'sorteiosVerifyRemainderIsTicketLegacy' : 'sorteiosVerifyRemainderIsTicket')}
                </p>
              </div>
            ) : null}

            {/* 其余名次：连号顺延，过最后一号回到 000。 */}
            {serials.length > 0 ? (
              <div className="srt-verify-card">
                <div className="srt-verify-seclabel">{t('sorteiosVerifyStep4')}</div>
                <div className="srt-verify-serials">
                  {serials.map((serial, index) => (
                    <span key={index} className={`srt-ticket${index === 0 ? ' is-win' : ''}`}>
                      {ticketLabel(serial)}
                    </span>
                  ))}
                </div>
                <p className="srt-verify-hint">
                  {t(serialBase === 1 ? 'sorteiosVerifyStep4HintLegacy' : 'sorteiosVerifyStep4Hint')}
                </p>
              </div>
            ) : null}

            {/* ── 第 3 层：这十位数哪来的（默认折叠）───────────────────── */}
            <button type="button" className={`srt-verify-src tap${showSource ? ' is-open' : ''}`}
              aria-expanded={showSource} onClick={() => setShowSource((open) => !open)}>
              <span className="srt-verify-src-icon" aria-hidden="true"><ShieldCheck size={15} /></span>
              <span className="srt-verify-src-text">
                <b>{t('sorteiosVerifySourceRow')}</b>
                <em>{t('sorteiosVerifySourceHint')}</em>
              </span>
              <ChevronDown className="srt-verify-src-chev" size={17} aria-hidden="true" />
            </button>

            {showSource ? (
              <>
                <div className="srt-verify-card">
                  <div className="srt-verify-step"><em>1</em><b>{t('sorteiosVerifyStep1')}</b></div>
                  <RoundAnchorCards anchors={round.anchors || []} roundCloseTs={roundCloseTimestamp(round)} />
                  <p className="srt-verify-hint">{t('sorteiosVerifyStep1Hint')}</p>
                </div>

                {/* highlight 必须是 null——Milhar 各档只取十位里的一个窗口所以
                    要高亮；抽奖用的是十位连成的整个数，传一个假窗口会让玩家
                    以为只有那几位参与计算。 */}
                <div className="srt-verify-card">
                  <div className="srt-verify-step"><em>2</em><b>{t('sorteiosVerifyStep2')}</b></div>
                  <RoundDigitsTable blockDigits={blockDigits} digits={digits} highlight={null} />
                  {digits ? (
                    <div className="srt-verify-d">
                      <span>{t('sorteiosVerifyD')}</span>
                      <b>{digits}</b>
                    </div>
                  ) : null}
                </div>
              </>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
